/* <alpha-video> — plays an mp4 and removes its grey/checkerboard background in real time (WebGL).
   Attrs: src, sat-max (0.18), luma-min (0.48), max-res (1600). Autoplays muted, loops, pauses off-screen. */
(() => {
  if (customElements.get('alpha-video')) return;
  const VS = 'attribute vec2 p;varying vec2 v;void main(){v=(p+1.0)*0.5;gl_Position=vec4(p,0.0,1.0);}';
  const FS = 'precision mediump float;varying vec2 v;uniform sampler2D t;uniform vec4 k;' +
    'void main(){vec3 c=texture2D(t,v).rgb;' +
    'float mx=max(c.r,max(c.g,c.b));float mn=min(c.r,min(c.g,c.b));' +
    'float sat=(mx-mn)/max(mx,0.0001);' +
    'float lum=dot(c,vec3(0.2126,0.7152,0.0722));' +
    'float bg=(1.0-smoothstep(k.x,k.y,sat))*smoothstep(k.z,k.w,lum);' +
    'float a=1.0-bg;gl_FragColor=vec4(c*a,a);}';
  class AlphaVideo extends HTMLElement {
    static get observedAttributes() { return ['src', 'sat-max', 'luma-min', 'max-res']; }
    connectedCallback() {
      if (!this._built) this._build();
      if (this._io) this._io.observe(this);
      if (this._gl && !this._raf) this._loop();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this._raf); this._raf = 0;
      if (this._io) this._io.unobserve(this);
      if (this._v) this._v.pause();
    }
    attributeChangedCallback(n) {
      if (!this._built) return;
      if (n === 'src') this._applySrc();
      else if (n === 'max-res') this._resize();
      else this._applyKey();
    }
    _build() {
      this._built = true;
      const sh = this.attachShadow({ mode: 'open' });
      const st = document.createElement('style');
      st.textContent = ':host{display:block;line-height:0}canvas,video{display:block;width:100%;height:auto}';
      sh.appendChild(st);
      const v = this._v = document.createElement('video');
      v.muted = true; v.loop = true; v.playsInline = true;
      v.setAttribute('playsinline', ''); v.setAttribute('muted', '');
      v.crossOrigin = 'anonymous'; v.preload = 'auto';
      const cv = this._cv = document.createElement('canvas');
      const gl = this._gl = cv.getContext('webgl', { alpha: true, premultipliedAlpha: true, preserveDrawingBuffer: true });
      if (!gl) { v.autoplay = true; sh.appendChild(v); this._applySrc(); return; } // fallback: original video
      sh.appendChild(cv);
      /* iOS Safari will not decode a media element that is detached from the document, so the
         source video has to live in the DOM even though the canvas is what gets painted. */
      v.autoplay = true; v.setAttribute('autoplay', '');
      v.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none';
      sh.appendChild(v);
      const mk = (ty, src) => { const s = gl.createShader(ty); gl.shaderSource(s, src); gl.compileShader(s); return s; };
      const pr = gl.createProgram();
      gl.attachShader(pr, mk(gl.VERTEX_SHADER, VS));
      gl.attachShader(pr, mk(gl.FRAGMENT_SHADER, FS));
      gl.linkProgram(pr); gl.useProgram(pr);
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(pr, 'p');
      gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      [[gl.TEXTURE_MIN_FILTER, gl.LINEAR], [gl.TEXTURE_MAG_FILTER, gl.LINEAR], [gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE]]
        .forEach(([a, b]) => gl.texParameteri(gl.TEXTURE_2D, a, b));
      this._uk = gl.getUniformLocation(pr, 'k');
      gl.clearColor(0, 0, 0, 0);
      this._lastT = -1;
      this._applyKey(); this._applySrc();
      v.addEventListener('loadedmetadata', () => {
        this.style.aspectRatio = v.videoWidth + ' / ' + v.videoHeight;
        this._resize();
      });
      this._ro = new ResizeObserver(() => this._resize());
      this._ro.observe(this);
      this._io = new IntersectionObserver(es => es.forEach(e => {
        if (e.isIntersecting) { this._inview = true; v.play().catch(() => {}); }
        else { this._inview = false; v.pause(); }
      }));
      /* Low Power Mode (and Safari's autoplay heuristics) reject play() until the user interacts.
         Retry on the first gesture and whenever the tab comes back, instead of giving up. */
      this._kick = () => { if (this._inview && v.paused) v.play().catch(() => {}); };
      ['touchstart', 'click', 'keydown'].forEach(n =>
        document.addEventListener(n, this._kick, { passive: true }));
      document.addEventListener('visibilitychange', this._kick);
    }
    _num(n, d) { const x = parseFloat(this.getAttribute(n)); return isNaN(x) ? d : x; }
    _applySrc() {
      const s = this.getAttribute('src');
      if (s && this._v.getAttribute('src') !== s) { this._v.src = s; this._lastT = -1; }
    }
    _applyKey() {
      if (!this._gl) return;
      const satMax = this._num('sat-max', 0.18), lumaMin = this._num('luma-min', 0.48);
      this._gl.uniform4f(this._uk, satMax * 0.35, satMax, lumaMin, Math.min(lumaMin + 0.10, 0.99));
      this._lastT = -1;
    }
    _resize() {
      const v = this._v;
      if (!this._gl || !v.videoWidth || !this.clientWidth) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const W = Math.round(Math.min(this.clientWidth * dpr, this._num('max-res', 1600), v.videoWidth));
      const H = Math.round(W * v.videoHeight / v.videoWidth);
      if (this._cv.width !== W || this._cv.height !== H) {
        this._cv.width = W; this._cv.height = H;
        this._gl.viewport(0, 0, W, H);
      }
      this._lastT = -1;
    }
    _loop() {
      this._raf = requestAnimationFrame(() => this._loop());
      const v = this._v, gl = this._gl;
      if (v.readyState < 2 || v.currentTime === this._lastT) return;
      if (!this._cv.width) this._resize();
      this._lastT = v.currentTime;
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, v);
      } catch (e) {
        /* tainted/refused upload: stop burning frames and let the host swap in its still */
        cancelAnimationFrame(this._raf); this._raf = 0;
        this.dispatchEvent(new CustomEvent('alpha-video:failed', { bubbles: true }));
        return;
      }
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (!this._drawn) {
        this._drawn = true;
        this.dispatchEvent(new CustomEvent('alpha-video:drawn', { bubbles: true }));
      }
    }
  }
  customElements.define('alpha-video', AlphaVideo);
})();
